import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Grid,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { SectionCard } from "./SectionCard";
import { SettingsSection } from "./SettingsSection";
import type { ClientManagementState } from "../types";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import type { ClientJobRole } from "../api/clientManagement";

interface JobRolesTabProps {
  effectiveClientId: string | null;
  managementState: ClientManagementState;
  onChange: (field: keyof ClientManagementState, value: string | boolean) => void;
  createJobRoleMutation: UseMutationResult<unknown, unknown, { name: string; is_active: boolean }>;
  updateJobRoleMutation: UseMutationResult<
    unknown,
    unknown,
    { jobRoleId: string; payload: { name?: string; is_active?: boolean } }
  >;
  deleteJobRoleMutation: UseMutationResult<unknown, unknown, string>;
  jobRolesQuery: UseQueryResult<{ data: ClientJobRole[] }>;
}

export const JobRolesTab: React.FC<JobRolesTabProps> = ({
  effectiveClientId,
  managementState,
  onChange,
  createJobRoleMutation,
  updateJobRoleMutation,
  deleteJobRoleMutation,
  jobRolesQuery,
}) => {
  const [edits, setEdits] = useState<Record<string, { name: string; is_active: boolean }>>({});

  useEffect(() => {
    if (!jobRolesQuery.data?.data?.length) return;
    setEdits((prev) => {
      const next = { ...prev };
      jobRolesQuery.data?.data.forEach((role) => {
        if (!next[role.id]) {
          next[role.id] = {
            name: role.name,
            is_active: role.is_active !== false,
          };
        }
      });
      return next;
    });
  }, [jobRolesQuery.data]);

  return (
    <SettingsSection
      title="Job Roles"
      description="Create and manage job roles for this client."
    >
      {!effectiveClientId && (
        <Alert severity="info">Select a client to manage job roles.</Alert>
      )}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 5 }}>
          <SectionCard
            title="Create job role"
            description="Add a new job role for this client."
          >
            <Stack spacing={2}>
              <TextField
                fullWidth
                label="Job role name"
                size="small"
                value={managementState.jobRoleName}
                onChange={(event) => onChange("jobRoleName", event.target.value)}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={managementState.jobRoleActive}
                    onChange={(event) =>
                      onChange("jobRoleActive", event.target.checked)
                    }
                  />
                }
                label="Active"
              />
              <Box display="flex" alignItems="center" gap={1}>
                <Button
                  variant="contained"
                  disabled={
                    !effectiveClientId ||
                    createJobRoleMutation.isPending ||
                    !managementState.jobRoleName.trim()
                  }
                  onClick={() =>
                    createJobRoleMutation.mutate({
                      name: managementState.jobRoleName.trim(),
                      is_active: managementState.jobRoleActive,
                    })
                  }
                >
                  Add job role
                </Button>
              </Box>
            </Stack>
          </SectionCard>
        </Grid>
        <Grid size={{ xs: 12, md: 7 }}>
          <SectionCard
            title="Existing job roles"
            description="Roles available for assignment."
          >
            {jobRolesQuery.isLoading ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} />
                <Typography variant="body2">Loading job roles...</Typography>
              </Stack>
            ) : jobRolesQuery.data?.data?.length ? (
              <TableContainer sx={{ width: "100%" }}>
                <Table size="small" sx={{ width: "100%" }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Job role</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {jobRolesQuery.data.data.map((role) => {
                      const edit = edits[role.id] ?? {
                        name: role.name,
                        is_active: role.is_active !== false,
                      };
                      const hasChanges =
                        edit.name !== role.name ||
                        edit.is_active !== (role.is_active !== false);
                      return (
                        <TableRow key={role.id}>
                          <TableCell>
                            <TextField
                              fullWidth
                              size="small"
                              label="Job role name"
                              value={edit.name}
                              onChange={(event) =>
                                setEdits((prev) => ({
                                  ...prev,
                                  [role.id]: {
                                    ...edit,
                                    name: event.target.value,
                                  },
                                }))
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={edit.is_active}
                                  onChange={(event) =>
                                    setEdits((prev) => ({
                                      ...prev,
                                      [role.id]: {
                                        ...edit,
                                        is_active: event.target.checked,
                                      },
                                    }))
                                  }
                                />
                              }
                              label={edit.is_active ? "Active" : "Inactive"}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <Button
                                variant="outlined"
                                size="small"
                                disabled={!hasChanges || updateJobRoleMutation.isPending}
                                onClick={() =>
                                  updateJobRoleMutation.mutate({
                                    jobRoleId: role.id,
                                    payload: {
                                      name: edit.name,
                                      is_active: edit.is_active,
                                    },
                                  })
                                }
                              >
                                Save
                              </Button>
                              <Button
                                color="error"
                                variant="text"
                                size="small"
                                disabled={deleteJobRoleMutation.isPending}
                                onClick={() => {
                                  if (
                                    window.confirm(
                                      "Delete this job role? This cannot be undone."
                                    )
                                  ) {
                                    deleteJobRoleMutation.mutate(role.id);
                                  }
                                }}
                              >
                                Delete
                              </Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No job roles found yet.
              </Typography>
            )}
          </SectionCard>
        </Grid>
      </Grid>
    </SettingsSection>
  );
};
